import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField } from '@mui/material';
import { ProfileLanguage, ProfileText } from 'models/organization-profile';
import ProfileLanguageSelect from './profile-language-select.component';
import { hasProfileText, initialProfileLanguage } from './profile-texts';

type ClinicProfileTabProps = {
    texts: ProfileText[];
    onChange: (texts: ProfileText[]) => void;
    labLanguage: ProfileLanguage;
};

/** DD-3662: what dentists read about the lab, written per language. */
const ClinicProfileTab: FC<ClinicProfileTabProps> = ({ texts, onChange, labLanguage }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [language, setLanguage] = useState<ProfileLanguage>(() => initialProfileLanguage(texts, labLanguage));
    const current = texts.find((text) => text.language === language);

    const updateField = (field: 'shortDescription' | 'longDescription', value: string) => {
        const others = texts.filter((text) => text.language !== language);
        onChange([...others, { ...current, language, [field]: value }]);
    };

    return (
        <Box>
            <Box my={2}>
                <ProfileLanguageSelect
                    value={language}
                    onChange={setLanguage}
                    writtenLanguages={texts
                        .filter((text) => hasProfileText(text.shortDescription, text.longDescription))
                        .map((text) => text.language)}
                    dataTest="clinic-profile.language"
                />
            </Box>
            <Box mb={2}>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={translate('lab-settings:SHORT_DESCRIPTION')}
                    helperText={translate('lab-settings:SHORT_DESCRIPTION_HELPER')}
                    value={current?.shortDescription ?? ''}
                    onChange={(event) => updateField('shortDescription', event.target.value)}
                    inputProps={{ 'data-test': 'clinic-profile.short-description' }}
                />
            </Box>
            <Box mb={3}>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={translate('lab-settings:LONG_DESCRIPTION')}
                    helperText={translate('lab-settings:LONG_DESCRIPTION_HELPER')}
                    value={current?.longDescription ?? ''}
                    onChange={(event) => updateField('longDescription', event.target.value)}
                    inputProps={{ 'data-test': 'clinic-profile.long-description' }}
                />
            </Box>
        </Box>
    );
};

export default ClinicProfileTab;
